import { useMemo } from 'react';
import type { FleetMissionType } from '@kaeliux/shared';
import {
    describeMissionAccountLock,
    getMissionAccountLock,
    resolveAccountStanding,
    type AccountStanding,
} from '@kaeliux/game-engine';
import { useAuthStore } from '../store/authStore';
import { usePlayerStore } from '../store/playerStore';
import { useNow } from './useNow';

export interface MissionLockNotice {
    message: string;
    /** The lock lifts by creating an account, rather than by waiting. */
    guest: boolean;
}

/**
 * The signed-in player's account standing (issue #332), from the session
 * (guest or not) and the guest history the server keeps on the player doc.
 * Display only — the server checks the same rules on every dispatch.
 */
export function useAccountStanding(): { standing: AccountStanding; now: number } {
    const isGuest = useAuthStore((s) => s.session?.isGuest ?? false);
    const guestAccount = usePlayerStore((s) => s.guestAccount);
    const now = useNow(30_000);
    const standing = useMemo(
        () => resolveAccountStanding(guestAccount ?? undefined, isGuest, now).standing,
        [guestAccount, isGuest, now],
    );
    return { standing, now };
}

/** Why each of `missions` is locked for this account right now; unlocked missions are left out. */
export function useMissionLocks(missions: readonly FleetMissionType[]): Partial<Record<FleetMissionType, MissionLockNotice>> {
    const { standing, now } = useAccountStanding();
    return useMemo(() => {
        const locks: Partial<Record<FleetMissionType, MissionLockNotice>> = {};
        for (const mission of missions) {
            const lock = getMissionAccountLock(mission, standing, now);
            if (lock) locks[mission] = { message: describeMissionAccountLock(mission, lock, now), guest: lock.reason === 'guest' };
        }
        return locks;
    }, [missions, standing, now]);
}
